import React, { useState } from 'react'
import Modal, { ModalHeader, ModalFooter } from '@app/components/Modal'
import * as S from '@app/components/Modal/styled'
import RoundedButton from '@app/ui/rounded-button/RoundedButton'
import { GeneratedForms } from '../../../types/Templates'
import { useSnackbar } from '@app/snackbar'
import { fetch } from '../../../../common/fetch'
import getEnv from '@app/environments/getEnv'
import theme from '@app/ui/theme'
import { downloadFolder } from './FinalRatingItem'
import FeatherIcon from '@ovaeasy/react-native-vector-icons/Feather'


type DownloadFormsModalProps = {
  isOpen: boolean
  onClose: () => void
  form: GeneratedForms | null
  templateId?: string
}

type DownloadOption = 'local' | 'email' | null

const DownloadFormsModal: React.FC<DownloadFormsModalProps> = ({
  isOpen,
  onClose,
  form,
  templateId,
}) => {
  const [selectedOption, setSelectedOption] = useState<DownloadOption>(null)
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState<string | null>(null)
  const [isSending, setIsSending] = useState(false)
  const [openSnackbar] = useSnackbar()

  const validateEmail = (emailValue: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(emailValue)
  }

  const handleLocalDownload = async () => {
    if (!form) return
    try {
      await downloadFolder(form.zipPath)
      onClose()
      setSelectedOption(null)
    } catch (error) {
      openSnackbar('Failed to download forms')
    }
  }

  const handleEmailSend = async () => {
    if (!form) return

    if (!email.trim()) {
      setEmailError('Email is required')
      return
    }

    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address')
      return
    }

    setEmailError(null)
    setIsSending(true)


    try {
      await fetch.post(`${getEnv().api}/ratings/forms/email-link`, {
        email: email.trim(),
        clubName: form.clubName || '',
        courseName: form.courseName || '',
        ratingName: form.name,
        templateId: templateId,
        formId: form.id,
      })

      openSnackbar('Download link sent successfully')
      onClose()
      setSelectedOption(null)
      setEmail('')
    } catch (error) {
      console.error('Error sending email:', error)
      openSnackbar('Failed to send email link')
    } finally {
      setIsSending(false)
    }
  }

  const handleClose = () => {
    onClose()
    setSelectedOption(null)
    setEmail('')
    setEmailError(null)
  }

  if (!form) return null

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      width="500px"
      height="auto"
      closeOnBackdropClick={true}
      showCloseButton={true}
    >
      <ModalHeader>
        <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600 }}>
          Download Forms
        </h2>
      </ModalHeader>
      <S.ModalScrollContent style={{ padding: '16px' }}>
        <p style={{ marginBottom: '20px', color: '#666' }}>
          Choose how you would like to download the forms for{' '}
          <strong>
            {form.clubName && form.courseName
              ? `${form.clubName} - ${form.courseName}`
              : 'this evaluation'}
          </strong>
          .
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '12px',
              border: `2px solid ${selectedOption === 'local' ? theme.colors.primary : '#e4e4e4'
                }`,
              borderRadius: '8px',
              cursor: 'pointer',
              backgroundColor:
                selectedOption === 'local' ? '#f0f7ff' : 'transparent',
            }}
            onClick={() => setSelectedOption('local')}
          >
            <input
              type="radio"
              name="downloadOption"
              value="local"
              checked={selectedOption === 'local'}
              onChange={() => setSelectedOption('local')}
              style={{ marginRight: '12px', cursor: 'pointer' }}
            />
 
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                Download zip file locally
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>
                Download the forms directly to your device
              </div>
            </div>
            <FeatherIcon
              name="download"
              size={24}
              color={
                selectedOption === 'local'
                  ? theme.colors.primary
                  : '#999'
              }
              style={{ marginRight: '12px' }}
            />
          </label>

          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '12px',
              border: `2px solid ${selectedOption === 'email' ? theme.colors.primary : '#e4e4e4'
                }`,
              borderRadius: '8px',
              cursor: 'pointer',
              backgroundColor:
                selectedOption === 'email' ? '#f0f7ff' : 'transparent',
            }}
            onClick={() => setSelectedOption('email')}
          >
            <input
              type="radio"
              name="downloadOption"
              value="email"
              checked={selectedOption === 'email'}
              onChange={() => setSelectedOption('email')}
              style={{ marginRight: '12px', cursor: 'pointer' }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                Send email downloadable link
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>
                Receive a download link via email
              </div>
            </div>
            <FeatherIcon
              name="mail"
              size={24}
              color={
                selectedOption === 'email'
                  ? theme.colors.primary
                  : '#999'
              }
              style={{ marginRight: '12px' }}
            />
          </label>
        </div>

        {selectedOption === 'email' && (
          <div style={{ marginTop: '20px' }}>
            <label
              htmlFor="email-input"
              style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: 500,
                fontSize: '14px',
              }}
            >
              Email address
            </label>
            <input
              id="email-input"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                if (emailError) setEmailError(null)
              }}
              placeholder="recipient@example.com"
              style={{
                width: '100%',
                padding: '10px 12px',
                border: `1px solid ${emailError ? '#d32f2f' : '#e4e4e4'}`,
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box',
              }}
              disabled={isSending}
            />
            {emailError && (
              <div
                style={{
                  marginTop: '6px',
                  color: '#d32f2f',
                  fontSize: '12px',
                }}
              >
                {emailError}
              </div>
            )}
          </div>
        )}
      </S.ModalScrollContent>
      <ModalFooter>
        <RoundedButton
          title="Cancel"
          onPress={handleClose}
          style={{
            backgroundColor: theme.colors.white,
            border: `1px solid ${theme.colors.secondary}`,
            color: theme.colors.secondary,
          }}
          textStyle={{
            color: theme.colors.secondary,
          }}
        />
        {selectedOption === 'local' && (
          <RoundedButton
            title="Download"
            onPress={handleLocalDownload}
            style={{
              backgroundColor: theme.colors.secondary2,
            }}
          />
        )}
        {selectedOption === 'email' && (
          <RoundedButton
            title={isSending ? 'Sending...' : 'Send Link'}
            onPress={handleEmailSend}
            disabled={isSending || !email.trim()}
            style={{
              backgroundColor: theme.colors.secondary2,
            }}
          />
        )}
      </ModalFooter>
    </Modal>
  )
}

export default DownloadFormsModal
